import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

export type PlanType = 'free' | 'pro' | 'premium';

interface Subscription {
  id: string;
  plan: PlanType;
  status: string;
  starts_at: string;
  ends_at: string | null;
}

interface PlanLimits {
  uploadsPerMonth: number;
  features: string[];
}

const PLAN_LIMITS: Record<PlanType, PlanLimits> = {
  free: {
    uploadsPerMonth: 5,
    features: ['Basic text extraction', 'Standard quality outputs', 'Email support'],
  },
  pro: {
    uploadsPerMonth: Infinity,
    features: ['Unlimited uploads', 'Advanced AI processing', 'PPT & document generation', 'Priority support', 'No watermarks'],
  },
  premium: {
    uploadsPerMonth: Infinity,
    features: ['Everything in Pro', 'Team collaboration', 'API access', 'Custom templates', 'Dedicated support', 'Early access features'],
  },
};

export function useSubscription() {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setSubscription(null);
      setLoading(false);
      return;
    }

    const fetchSubscription = async () => {
      try {
        // Use the safe view that excludes sensitive payment fields
        const { data, error } = await supabase
          .from('user_subscriptions' as any)
          .select('*')
          .eq('user_id', user.id)
          .eq('status', 'active')
          .maybeSingle();

        if (error) {
          console.error('Error fetching subscription:', error);
        }

        setSubscription(data as unknown as Subscription | null);
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSubscription();
  }, [user]);

  const currentPlan: PlanType = subscription?.plan as PlanType || 'free';
  const planLimits = PLAN_LIMITS[currentPlan];

  const isExpired = subscription?.ends_at 
    ? new Date(subscription.ends_at) < new Date() 
    : false;

  return {
    subscription,
    currentPlan: isExpired ? 'free' : currentPlan,
    planLimits,
    loading,
    isExpired,
    isPro: currentPlan === 'pro' && !isExpired,
    isPremium: currentPlan === 'premium' && !isExpired,
  };
}
